/**
 * El paso 4: el wizard propiamente. Solo frena en lo que no sabe.
 *
 * Arriba, plegadas, las lineas que matchearon por codigo exacto: entran sin
 * preguntar. Despues, de a una, las dudosas y las que no tienen producto. Cada
 * respuesta va al backend y la linea sale de la cola; la siguiente sube sola.
 * "Saltar por ahora" la manda al final, no la resuelve.
 *
 * La cola se recalcula en cada render sobre las filas del servidor: la verdad
 * de "que falta" vive en la base (`decidida`), y si el operador cierra la
 * pestana y vuelve, retoma donde estaba.
 */
import { useMemo, useState } from "react";
import { ArrowRight, ChevronDown, PlusCircle } from "lucide-react";
import type { Moneda } from "@tora/shared";

import { mostrarCantidad } from "@/components/catalogo/Campos";
import { Importe } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EstadoVacio } from "@/components/ui/estado-vacio";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { aNumero, type Rubro } from "@/lib/tipos";

import type { CambiosFila, NuevaFila } from "./consultas";
import {
  cantidadEfectiva,
  duplicadas,
  esManual,
  costoDelProducto,
  listasSinPreguntar,
  pendientesDelWizard,
  resueltas,
  type Compra,
  type DatosAlta,
  type FilaCompra,
} from "./logica";
import { LineaManual } from "./LineaManual";
import { TarjetaLinea } from "./TarjetaLinea";

export interface FlujoLineasProps {
  compra: Compra;
  filas: ReadonlyArray<FilaCompra>;
  cotizacion: number;
  /** La moneda en la que cobra el proveedor: decide el costo del producto. */
  monedaProveedor: Moneda | null;
  rubros: ReadonlyArray<Rubro>;
  rubroSugeridoId: number | null;
  guardando: boolean;
  onActualizarFila: (filaId: number, cambios: CambiosFila) => Promise<void>;
  onAgregarFila: (fila: NuevaFila) => Promise<void>;
  onBuscarProducto: (fila: FilaCompra) => void;
  onVolver: () => void;
  onListo: () => void;
}

export function FlujoLineas({
  compra,
  filas,
  cotizacion,
  monedaProveedor,
  rubros,
  rubroSugeridoId,
  guardando,
  onActualizarFila,
  onAgregarFila,
  onBuscarProducto,
  onVolver,
  onListo,
}: FlujoLineasProps): JSX.Element {
  const [saltadas, setSaltadas] = useState<number[]>([]);
  // Una linea ya resuelta que el operador quiere volver a mirar.
  const [reabierta, setReabierta] = useState<number | null>(null);
  // Una compra a mano sin lineas arranca con el formulario abierto: es lo
  // unico que se puede hacer.
  const [agregando, setAgregando] = useState(() => esManual(compra) && filas.length === 0);

  const listas = useMemo(() => listasSinPreguntar(filas), [filas]);
  const decididas = useMemo(() => resueltas(filas), [filas]);
  const pendientes = useMemo(() => {
    const base = pendientesDelWizard(filas);
    // Las saltadas van al final, en el orden en que se saltaron.
    const noSaltadas = base.filter((f) => !saltadas.includes(f.id));
    const alFinal = saltadas
      .map((id) => base.find((f) => f.id === id))
      .filter((f): f is FilaCompra => f !== undefined);
    return [...noSaltadas, ...alFinal];
  }, [filas, saltadas]);
  const dobles = useMemo(() => duplicadas(filas), [filas]);

  const actual =
    reabierta !== null ? (filas.find((f) => f.id === reabierta) ?? null) : (pendientes[0] ?? null);
  const indiceActual = actual
    ? Math.max(
        0,
        pendientes.findIndex((f) => f.id === actual.id),
      )
    : 0;

  async function decidir(fila: FilaCompra, cambios: CambiosFila): Promise<void> {
    await onActualizarFila(fila.id, cambios);
    setReabierta(null);
    setSaltadas((s) => s.filter((id) => id !== fila.id));
  }

  function saltar(fila: FilaCompra): void {
    setReabierta(null);
    setSaltadas((s) => (s.includes(fila.id) ? s : [...s, fila.id]));
  }

  return (
    <div className="space-y-4">
      {listas.length > 0 ? (
        <Plegable
          titulo={`${listas.length} ${listas.length === 1 ? "producto listo" : "productos listos"}: entran sin preguntar`}
          detalle="Matchearon por código exacto con un producto del catálogo."
          tono="success"
        >
          <ListaCompacta filas={listas} onRevisar={(f) => setReabierta(f.id)} />
        </Plegable>
      ) : null}

      {decididas.length > 0 ? (
        <Plegable
          titulo={`${decididas.length} ${decididas.length === 1 ? "producto resuelto" : "productos resueltos"}`}
          detalle="Las decidiste vos. Podés volver a mirar cualquiera."
          tono="neutral"
        >
          <ListaCompacta filas={decididas} onRevisar={(f) => setReabierta(f.id)} />
        </Plegable>
      ) : null}

      {dobles.size > 0 ? (
        <Aviso tono="warning">
          {[...dobles.values()]
            .map(
              (grupo) =>
                `${grupo.length} renglones van al mismo producto «${grupo[0]?.producto_nombre ?? ""}»`,
            )
            .join("; ")}
          . No se suman solas: entran como ingresos separados. Si es un error, dejá una afuera.
        </Aviso>
      ) : null}

      {actual ? (
        <TarjetaLinea
          key={actual.id}
          fila={actual}
          {...(reabierta !== null
            ? {}
            : { posicion: { indice: indiceActual, total: pendientes.length } })}
          cotizacion={cotizacion}
          costoProducto={costoDelProducto(actual, compra, monedaProveedor)}
          rubros={rubros}
          rubroSugeridoId={rubroSugeridoId}
          guardando={guardando}
          onConfirmarMatch={() =>
            void decidir(actual, { accion: "INGRESAR", producto_id: actual.producto_id })
          }
          onBuscarOtro={() => onBuscarProducto(actual)}
          onCrear={(alta: DatosAlta) =>
            void decidir(actual, {
              accion: "CREAR_E_INGRESAR",
              producto_id: null,
              nuevo_nombre: alta.nombre.trim(),
              nuevo_rubro_id: alta.rubroId,
              nuevo_codigo_interno: alta.codigoInterno,
              nuevo_precio_manual: alta.precio,
              nuevo_markup_pct: alta.markup,
            })
          }
          onIgnorar={() => void decidir(actual, { accion: "IGNORAR" })}
          onSaltar={reabierta !== null ? () => setReabierta(null) : () => saltar(actual)}
          onCantidad={(recibida, motivo) =>
            void onActualizarFila(actual.id, {
              cantidad_recibida: recibida,
              motivo_diferencia: motivo,
            })
          }
          onPrecio={(costoNeto) => void onActualizarFila(actual.id, { costo_neto: costoNeto })}
        />
      ) : filas.length === 0 ? (
        agregando ? null : (
          <EstadoVacio
            titulo="Todavía no hay productos"
            detalle="Agregá la primera buscando el producto en el catálogo."
          />
        )
      ) : (
        <Card padding="normal">
          <TituloSeccion descripcion="Todos los productos tienen destino. Pasá a revisar qué va a pasar al aplicar.">
            No queda nada por resolver
          </TituloSeccion>
        </Card>
      )}

      {agregando ? (
        <LineaManual
          moneda={compra.moneda ?? "ARS"}
          cotizacion={aNumero(compra.cotizacion) || cotizacion}
          proveedorId={compra.proveedor_id}
          guardando={guardando}
          onAgregar={async (fila) => {
            await onAgregarFila(fila);
            // En una compra a mano se viene de a una: el formulario queda
            // abierto para la siguiente.
            if (!esManual(compra)) setAgregando(false);
          }}
          onCancelar={() => setAgregando(false)}
        />
      ) : (
        <Button
          type="button"
          variant="dashed"
          onClick={() => setAgregando(true)}
          disabled={guardando}
        >
          <PlusCircle aria-hidden="true" />
          Agregar un producto
        </Button>
      )}

      <div className="flex flex-wrap justify-between gap-2">
        {esManual(compra) ? (
          <span />
        ) : (
          <Button type="button" variant="outline" onClick={onVolver} disabled={guardando}>
            Volver al encabezado
          </Button>
        )}
        <Button type="button" onClick={onListo} disabled={guardando}>
          {pendientes.length === 0 ? "Ir a revisar" : `Ir a revisar (faltan ${pendientes.length})`}
        </Button>
      </div>
    </div>
  );
}

function Plegable({
  titulo,
  detalle,
  tono,
  children,
}: {
  titulo: string;
  detalle: string;
  tono: "success" | "neutral";
  children: React.ReactNode;
}): JSX.Element {
  // La tarjeta pone el tinte; el <details> adentro solo pliega.
  return (
    <Card tono={tono === "success" ? "success" : "none"} padding="none">
      <details className="group">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-4">
          <span className="min-w-0">
            <span className="block text-sm font-medium">{titulo}</span>
            <span className="block text-xs text-muted-foreground">{detalle}</span>
          </span>
          <ChevronDown
            className="size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180"
            aria-hidden="true"
          />
        </summary>
        <div className="border-t border-border p-2">{children}</div>
      </details>
    </Card>
  );
}

function ListaCompacta({
  filas,
  onRevisar,
}: {
  filas: ReadonlyArray<FilaCompra>;
  onRevisar: (fila: FilaCompra) => void;
}): JSX.Element {
  return (
    <ul className="divide-y divide-border">
      {filas.map((f) => {
        const destino =
          f.accion === "IGNORAR" ? (
            "queda afuera"
          ) : f.accion === "CREAR_E_INGRESAR" && f.producto_id === null ? (
            `se crea «${f.nuevo_nombre ?? ""}»`
          ) : (
            <>
              <ArrowRight className="inline size-3 align-[-1px]" aria-hidden="true" />{" "}
              {f.producto_nombre ?? ""}
            </>
          );
        return (
          <li key={f.id} className="flex items-center justify-between gap-3 px-2 py-2 text-sm">
            <span className="min-w-0">
              <span className="block truncate">{f.descripcion}</span>
              <span className="block truncate text-xs text-muted-foreground">{destino}</span>
            </span>
            <span className="flex shrink-0 items-center gap-3">
              <span className="text-xs tabular-nums text-muted-foreground">
                {mostrarCantidad(cantidadEfectiva(f))} u. ·{" "}
                <Importe monto={aNumero(f.costo_final)} moneda={f.moneda} tamano="sm" />
              </span>
              <Button type="button" variant="ghost" size="sm" onClick={() => onRevisar(f)}>
                Revisar
              </Button>
            </span>
          </li>
        );
      })}
    </ul>
  );
}
